/**
 * Converts from Gemini coordinates (0-1000, origin top-left)
 * to PDF coordinates (612x792 points, origin bottom-left).
 */
export function geminiConvertBoundingBoxToPdfCoordinates(
  {
    xMin,
    yMin,
    xMax,
    yMax,
  }: {
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
  },
  { angleInDegrees, width: PDF_WIDTH, height: PDF_HEIGHT } = { angleInDegrees: 0, width: 8.5 * 72, height: 11 * 72 },
): {
  xMin: number;
  yMin: number;
  xMax: number;
  yMax: number;
} {
  const INPUT_WIDTH = 1000;
  const INPUT_HEIGHT = 1000;
  const normalizedAngle = ((angleInDegrees % 360) + 360) % 360;

  let result: {
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
  };

  switch (normalizedAngle) {
    case 0: // No rotation
      result = {
        xMin: Math.round((xMin / INPUT_WIDTH) * PDF_WIDTH),
        yMin: Math.round(PDF_HEIGHT - (yMax / INPUT_HEIGHT) * PDF_HEIGHT),
        xMax: Math.round((xMax / INPUT_WIDTH) * PDF_WIDTH),
        yMax: Math.round(PDF_HEIGHT - (yMin / INPUT_HEIGHT) * PDF_HEIGHT),
      };
      break;

    case 90: // 90 degrees clockwise
      // When rotated 90°, x becomes y and y becomes (width - x)
      result = {
        xMin: Math.round((yMin / INPUT_HEIGHT) * PDF_WIDTH),
        yMin: Math.round(PDF_HEIGHT - ((INPUT_WIDTH - xMax) / INPUT_WIDTH) * PDF_HEIGHT),
        xMax: Math.round((yMax / INPUT_HEIGHT) * PDF_WIDTH),
        yMax: Math.round(PDF_HEIGHT - ((INPUT_WIDTH - xMin) / INPUT_WIDTH) * PDF_HEIGHT),
      };
      break;

    case 180: // 180 degrees
      // When rotated 180°, both x and y are flipped
      result = {
        xMin: Math.round(((INPUT_WIDTH - xMax) / INPUT_WIDTH) * PDF_WIDTH),
        yMin: Math.round((yMin / INPUT_HEIGHT) * PDF_HEIGHT),
        xMax: Math.round(((INPUT_WIDTH - xMin) / INPUT_WIDTH) * PDF_WIDTH),
        yMax: Math.round((yMax / INPUT_HEIGHT) * PDF_HEIGHT),
      };
      break;

    case 270: // 270 degrees clockwise (or 90 counter-clockwise)
      // When rotated 270°, x becomes (height - y) and y becomes x
      result = {
        xMin: Math.round(((INPUT_HEIGHT - yMax) / INPUT_HEIGHT) * PDF_WIDTH),
        yMin: Math.round(PDF_HEIGHT - (xMax / INPUT_WIDTH) * PDF_HEIGHT),
        xMax: Math.round(((INPUT_HEIGHT - yMin) / INPUT_HEIGHT) * PDF_WIDTH),
        yMax: Math.round(PDF_HEIGHT - (xMin / INPUT_WIDTH) * PDF_HEIGHT),
      };
      break;

    default:
      throw new Error(`Unsupported rotation angle: ${angleInDegrees}. Only 0, 90, 180, and 270 degrees are supported.`);
  }

  return result;
}

/**
 * Converts from Adobe PDF coordinates (612x792 points, origin bottom-left)
 * to Gemini coordinates (0-1000, origin top-left).
 * This is the inverse of geminiConvertBoundingBoxToPdfCoordinates.
 */
export function pdfConvertBoundingBoxToGeminiCoordinates(
  {
    xMin,
    yMin,
    xMax,
    yMax,
  }: {
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
  },
  { angleInDegrees, width: PDF_WIDTH, height: PDF_HEIGHT } = { angleInDegrees: 0, width: 8.5 * 72, height: 11 * 72 },
): {
  xMin: number;
  yMin: number;
  xMax: number;
  yMax: number;
} {
  const OUTPUT_WIDTH = 1000;
  const OUTPUT_HEIGHT = 1000;
  const normalizedAngle = ((angleInDegrees % 360) + 360) % 360;

  let result: {
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
  };

  switch (normalizedAngle) {
    case 0: // No rotation
      result = {
        xMin: Math.round((xMin / PDF_WIDTH) * OUTPUT_WIDTH),
        yMin: Math.round(((PDF_HEIGHT - yMax) / PDF_HEIGHT) * OUTPUT_HEIGHT),
        xMax: Math.round((xMax / PDF_WIDTH) * OUTPUT_WIDTH),
        yMax: Math.round(((PDF_HEIGHT - yMin) / PDF_HEIGHT) * OUTPUT_HEIGHT),
      };
      break;

    case 90: // 90 degrees clockwise
      result = {
        xMin: Math.round(OUTPUT_WIDTH - ((PDF_HEIGHT - yMax) / PDF_HEIGHT) * OUTPUT_WIDTH),
        yMin: Math.round((xMin / PDF_WIDTH) * OUTPUT_HEIGHT),
        xMax: Math.round(OUTPUT_WIDTH - ((PDF_HEIGHT - yMin) / PDF_HEIGHT) * OUTPUT_WIDTH),
        yMax: Math.round((xMax / PDF_WIDTH) * OUTPUT_HEIGHT),
      };
      break;

    case 180: // 180 degrees
      result = {
        xMin: Math.round(OUTPUT_WIDTH - (xMax / PDF_WIDTH) * OUTPUT_WIDTH),
        yMin: Math.round((yMin / PDF_HEIGHT) * OUTPUT_HEIGHT),
        xMax: Math.round(OUTPUT_WIDTH - (xMin / PDF_WIDTH) * OUTPUT_WIDTH),
        yMax: Math.round((yMax / PDF_HEIGHT) * OUTPUT_HEIGHT),
      };
      break;

    case 270: // 270 degrees clockwise
      result = {
        xMin: Math.round(((PDF_HEIGHT - yMax) / PDF_HEIGHT) * OUTPUT_WIDTH),
        yMin: Math.round(OUTPUT_HEIGHT - (xMax / PDF_WIDTH) * OUTPUT_HEIGHT),
        xMax: Math.round(((PDF_HEIGHT - yMin) / PDF_HEIGHT) * OUTPUT_WIDTH),
        yMax: Math.round(OUTPUT_HEIGHT - (xMin / PDF_WIDTH) * OUTPUT_HEIGHT),
      };
      break;

    default:
      throw new Error(`Unsupported rotation angle: ${angleInDegrees}. Only 0, 90, 180, and 270 degrees are supported.`);
  }

  return result;
}
